import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { profileAvatar } from "../component/ImageList";
import baseURL from "../component/baseURL";
import showNotification from '../component/Notification';
import stringsoflanguages from '../component/stringsoflanguages';

const MY_LISTINGS_GRID_SCROLL_KEY = 'yesmls_myListings_gridScrollTop';
const MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY = 'yesmls_myListings_restoreScrollPending';

function readMyListingsRestorePending() {
    try {
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY) === '1';
    } catch {
        return false;
    }
}



export default function MyListings() {
    const navigate = useNavigate();
    const location = useLocation();
    const [userID, setUserID] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [myListings, setMyListings] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const [storedIsMember, setStoredIsMember] = useState('');
    const [storedMemberId, setStoredMemberId] = useState('');
    const gearMenuRef = useRef(null);
    /** `.my-listings-cards-grid` is the vertical scroll container (same as Global Portfolio). */
    const listScrollRef = useRef(null);
    const [gridHiddenForScrollRestore, setGridHiddenForScrollRestore] = useState(() => readMyListingsRestorePending());

    const fromSidebar = location.state?.fromSidebar === true;
    const stateIsMember = location.state?.is_member;
    const searchParams = new URLSearchParams(location.search || '');
    const isMemberDisplay = !fromSidebar && (stateIsMember === 1 || stateIsMember === '1' || searchParams.get('is_member') === '1' || localStorage.getItem('is_member') === '1');
    const memberFname = location.state?.member_fname ?? searchParams.get('member_fname') ?? '';
    const memberLname = location.state?.member_lname ?? searchParams.get('member_lname') ?? '';
    const memberDisplayName = [memberFname].filter(Boolean).join(' ').trim() || '—';

    const searchLower = (searchQuery || '').trim().toLowerCase();
    const filteredListings = searchLower
        ? myListings.filter((listing) => {
            const title = String(listing.ListingId ?? listing.listing_id ?? '').toLowerCase();
            const propertyType = (listing.PropertyType ?? '').toLowerCase();
            const priceStr = String(listing.ListPrice ?? '').toLowerCase();
            return title.includes(searchLower) || propertyType.includes(searchLower) || priceStr.includes(searchLower);
        })
        : myListings;

    function saveListingGridScrollAndGoToDetails(listing, id) {
        const el = listScrollRef.current;
        const gridScroll = el ? el.scrollTop : 0;
        if (el) {
            sessionStorage.setItem(MY_LISTINGS_GRID_SCROLL_KEY, String(gridScroll));
        }
        sessionStorage.setItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY, '1');

        const listState = { scrollPosition: gridScroll };
        if (location.state?.returnTo) {
            listState.returnTo = location.state.returnTo;
        }
        if (isMemberDisplay) {
            listState.is_member =
                location.state?.is_member ??
                searchParams.get('is_member') ??
                localStorage.getItem('is_member');
            listState.member_id =
                location.state?.member_id ??
                searchParams.get('member_id') ??
                localStorage.getItem('member_id') ??
                '';
            if (memberFname) listState.member_fname = memberFname;
            if (memberLname) listState.member_lname = memberLname;
        } else {
            listState.fromSidebar = true;
        }

        navigate('/portfolio-details', {
            state: {
                listingID: listing.listing_id ?? listing.ListingId ?? id,
                from: 'my-listings',
                returnTo: {
                    path: '/home/my-listings',
                    state: listState,
                },
                ...(isMemberDisplay && {
                    is_member: listState.is_member,
                    member_id: listState.member_id,
                }),
            },
        });
    }

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        setLoading(true);
        const userid = localStorage.getItem('user_id');
        setUserID(userid);

        if (location.state?.fromSidebar) {
            localStorage.removeItem('is_member');
            localStorage.removeItem('member_id');
            localStorage.removeItem('member_fname');
            localStorage.removeItem('member_lname');
            setStoredIsMember('');
            setStoredMemberId('');
        }

        fetchMyListingsAPI(userid);

        const handleClickOutside = (event) => {
            if (dropdownRef.current && buttonRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        const handleGearClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('mousedown', handleGearClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('mousedown', handleGearClickOutside);
        };
    }, [location.state, location.search]);

    /** Persist grid scroll while browsing */
    useEffect(() => {
        if (loading) return undefined;
        const el = listScrollRef.current;
        if (!el) return undefined;
        let timeoutId;
        const onScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                sessionStorage.setItem(MY_LISTINGS_GRID_SCROLL_KEY, String(el.scrollTop));
            }, 120);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('scroll', onScroll);
        };
    }, [loading]);

    /** Restore scroll before paint + fade-in after back from portfolio details */
    useLayoutEffect(() => {
        if (loading) return undefined;
        if (sessionStorage.getItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY) !== '1') {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const raw = sessionStorage.getItem(MY_LISTINGS_GRID_SCROLL_KEY);
        if (raw == null || raw === '') {
            sessionStorage.removeItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const y = Number(raw);
        if (!Number.isFinite(y) || y < 0) {
            sessionStorage.removeItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const el = listScrollRef.current;
        if (!el) {
            sessionStorage.removeItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        el.scrollTop = y;
        sessionStorage.removeItem(MY_LISTINGS_RESTORE_SCROLL_PENDING_KEY);

        if (reducedMotion) {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        requestAnimationFrame(() => {
            setGridHiddenForScrollRestore(false);
        });
        return undefined;
    }, [loading, filteredListings.length]);

    function makeFav(listingId) {
        if (!listingId || !userID) {
            console.warn('Missing data to mark favorite', { listingId, userID });
            return;
        }
        const propertyIndex = myListings.findIndex((p) => (p.listing_id ?? p.ListingId) === listingId);
        if (propertyIndex === -1) return;
        const property = myListings[propertyIndex];
        const isCurrentlyFavorite = property.is_favorite == '1';
        const newFavoriteStatus = isCurrentlyFavorite ? '0' : '1';
        const prevList = [...myListings];
        const updatedList = [...myListings];
        updatedList[propertyIndex] = { ...property, is_favorite: newFavoriteStatus };
        setMyListings(updatedList);
        if (newFavoriteStatus === '1') {
            showNotification('Success', 'Add to favorite', 'success');
        } else {
            showNotification('Success', 'Remove to favorite', 'success');
        }
        const formdata = new FormData();
        formdata.append('user_id', userID);
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        formdata.append('listing_id', listingId);
        formdata.append('is_favorite', newFavoriteStatus);
        fetch(baseURL.URL + '/portfolio/listing_favorites', { method: 'POST', body: formdata })
            .then((res) => res.json())
            .then((responseJson) => {
                if (responseJson.server?.code !== 200) {
                    setMyListings(prevList);
                    showNotification('Error', responseJson.server?.message || 'Unable to update favorite', 'danger');
                }
            })
            .catch(() => {
                setMyListings(prevList);
                showNotification('Error', 'Something went wrong while updating favorite', 'danger');
            });
    }

    function fetchMyListingsAPI(userid) {
        const fromSidebar = location.state?.fromSidebar === true;
        const stateIsMember = location.state?.is_member;
        const searchParams = new URLSearchParams(window.location.search);
        const isMember = !fromSidebar && (stateIsMember === 1 || stateIsMember === '1' || searchParams.get('is_member') === '1' || localStorage.getItem('is_member') === '1');
        if (isMember) {
            const memberID = (location.state?.member_id ?? searchParams.get('member_id')) || localStorage.getItem('member_id') || '';
            let formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            formdata.append("is_member", '1');
            formdata.append("member_id", memberID);
            fetch(baseURL.URL + '/portfolio/my_listings', {
                method: 'POST',
                body: formdata
            }).then((response) => response.json())
                .then((responseJson) => {
                    if (responseJson.server?.code === 200) {
                        const portfolio = responseJson.response?.portfolio;
                        setMyListings(Array.isArray(portfolio) ? portfolio : []);
                    } else {
                        Toast.showWithGravity(responseJson.server?.message ?? 'Error', Toast.SHORT, Toast.BOTTOM);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        } else {
            let formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            fetch(baseURL.URL + '/portfolio/my_listings', {
                method: 'POST',
                body: formdata
            }).then((response) => response.json())
                .then((responseJson) => {
                    if (responseJson.server?.code === 200) {
                        const portfolio = responseJson.response?.portfolio;
                        setMyListings(Array.isArray(portfolio) ? portfolio : []);
                    } else {
                        Toast.showWithGravity(responseJson.server?.message ?? 'Error', Toast.SHORT, Toast.BOTTOM);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        }
    }




    if (loading) {
        return (
            <div className="content-right my-listings-page page-loader-wrapper">
                <div className="react-js-loader">
                    <div className="react-js-loader-spinner"></div>
                    <p className="react-js-loader-text">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="content-right my-listings-page own-listings-page">
                {/* Search and Filter Section */}
                <div className="search-filter-section">
                    <div className="search-bar">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="search-icon"
                        >
                            <path
                                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M17.5 17.5L13.875 13.875"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by title / property type / price"
                            className="search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <button
                            ref={buttonRef}
                            className="filter-btn filter-btn-listings"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M8.19119 3.15595C7.30475 2.94802 6.38222 2.94802 5.49578 3.15595C4.33479 3.42828 3.42828 4.33479 3.15595 5.49578C2.94802 6.38222 2.94802 7.30475 3.15595 8.19119C3.42828 9.35218 4.33479 10.2587 5.49578 10.531C6.38222 10.739 7.30475 10.739 8.19119 10.531C9.35218 10.2587 10.2587 9.35218 10.531 8.19119C10.739 7.30475 10.739 6.38222 10.531 5.49578C10.2587 4.33479 9.35218 3.42828 8.19119 3.15595Z" fill="white" />
                                <path d="M8.19119 13.469C7.30475 13.261 6.38222 13.261 5.49578 13.469C4.33479 13.7413 3.42828 14.6478 3.15595 15.8088C2.94802 16.6952 2.94802 17.6178 3.15595 18.5042C3.42828 19.6652 4.33479 20.5717 5.49578 20.8441C6.38222 21.052 7.30475 21.052 8.19119 20.8441C9.35218 20.5717 10.2587 19.6652 10.531 18.5042C10.739 17.6178 10.739 16.6952 10.531 15.8088C10.2587 14.6478 9.35218 13.7413 8.19119 13.469Z" fill="white" />
                                <path d="M18.5042 3.15595C17.6178 2.94802 16.6953 2.94802 15.8088 3.15595C14.6478 3.42828 13.7413 4.33479 13.469 5.49578C13.261 6.38222 13.261 7.30475 13.469 8.19119C13.7413 9.35218 14.6478 10.2587 15.8088 10.531C16.6953 10.739 17.6178 10.739 18.5042 10.531C19.6652 10.2587 20.5717 9.35218 20.8441 8.19119C21.052 7.30475 21.052 6.38222 20.8441 5.49578C20.5717 4.33479 19.6652 3.42828 18.5042 3.15595Z" fill="white" />
                                <path d="M18.5042 13.469C17.6178 13.261 16.6953 13.261 15.8088 13.469C14.6478 13.7413 13.7413 14.6478 13.469 15.8088C13.261 16.6952 13.261 17.6178 13.469 18.5042C13.7413 19.6652 14.6478 20.5717 15.8088 20.8441C16.6953 21.052 17.6178 21.052 18.5042 20.8441C19.6652 20.5717 20.5717 19.6652 20.8441 18.5042C21.052 17.6178 21.052 16.6952 20.8441 15.8088C20.5717 14.6478 19.6652 13.7413 18.5042 13.469Z" fill="white" />
                            </svg>
                        </button>
                        {isDropdownOpen && (
                            <div ref={dropdownRef} className="listings-dropdown">
                                <div className="dropdown-menu-content">
                                    <div className="dropdown-menu-item" onClick={() => setIsDropdownOpen(false)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 16.125C11.5858 16.125 11.25 15.7892 11.25 15.375V12.75H8.625C8.21078 12.75 7.875 12.4142 7.875 12C7.875 11.5858 8.21078 11.25 8.625 11.25H11.25V8.625C11.25 8.21078 11.5858 7.875 12 7.875C12.4142 7.875 12.75 8.21078 12.75 8.625V11.25H15.375C15.7892 11.25 16.125 11.5858 16.125 12C16.125 12.4142 15.7892 12.75 15.375 12.75H12.75V15.375C12.75 15.7892 12.4142 16.125 12 16.125Z" fill="white" />
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.75 0.75C3.43629 0.75 0.75 3.43629 0.75 6.75V17.25C0.75 20.5637 3.43629 23.25 6.75 23.25H17.25C20.5637 23.25 23.25 20.5637 23.25 17.25V7.72774C23.25 6.89621 22.9049 6.10196 22.2969 5.53459L18.0351 1.55683C17.4796 1.03836 16.748 0.75 15.9881 0.75H6.75ZM2.25 6.75C2.25 4.26472 4.26472 2.25 6.75 2.25H15.75V4.5C15.75 6.15686 17.0931 7.5 18.75 7.5H21.7326C21.7441 7.57489 21.75 7.65101 21.75 7.72774V17.25C21.75 19.7353 19.7353 21.75 17.25 21.75H6.75C4.26472 21.75 2.25 19.7353 2.25 17.25V6.75ZM17.25 4.5V2.87591L20.5972 6H18.75C17.9216 6 17.25 5.32841 17.25 4.5Z" fill="white" />
                                        </svg>
                                        <span>Add Listings</span>
                                    </div>
                                    <div className="dropdown-menu-item" onClick={() => setIsDropdownOpen(false)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="24" viewBox="0 0 26 24" fill="none">
                                            <path d="M21.6426 8.03096C21.5536 10.6969 21.2851 18.8108 13.4271 21.919C13.2906 21.9728 13.1458 22.0002 13.0002 22.0002C12.8553 22.0002 12.7097 21.9728 12.5738 21.919C11.7982 21.6133 11.0673 21.2451 10.4007 20.8273C9.90615 20.5167 9.77745 19.8946 10.1144 19.4377C10.4503 18.9807 11.1243 18.8624 11.6193 19.173C12.0478 19.4425 12.5106 19.6866 12.9999 19.9026C18.5205 17.4658 19.3447 11.9497 19.4757 7.96928C19.4937 7.42784 19.9752 7.00016 20.5581 7.00016C20.5698 7.00016 20.5815 7.00016 20.5919 7.00064C21.1907 7.01768 21.6605 7.47848 21.6426 8.03096ZM21.4382 3.61376L6.27213 21.6142C6.05841 21.8672 5.73887 21.9999 5.41621 21.9999C5.18351 21.9999 4.94925 21.9315 4.75191 21.789C4.27949 21.4501 4.19447 20.8222 4.56159 20.3857L6.96607 17.5321C4.33357 13.681 4.33357 8.87288 4.33357 6.00008C4.33357 5.44784 4.81873 5 5.41699 5C7.50167 5 9.30165 5 12.2345 2.29304C12.6577 1.90232 13.3431 1.90232 13.7664 2.29304C15.1608 3.58016 16.3976 4.34864 17.6555 4.71056C17.6859 4.71944 17.7072 4.74032 17.7363 4.75136L19.7292 2.3864C20.0963 1.95128 20.7765 1.8728 21.2494 2.21072C21.7208 2.54984 21.8064 3.1784 21.4382 3.61376ZM8.41323 15.8146L16.3614 6.38216C15.2414 5.96504 14.1431 5.3048 13.0025 4.35944C10.3739 6.53192 8.31287 6.92408 6.50379 6.98696C6.52901 9.47912 6.75287 12.9476 8.41323 15.8146Z" fill="white" />
                                        </svg>
                                        <span>Inactive Listings</span>
                                    </div>
                                    <div className="dropdown-menu-item" onClick={() => setIsDropdownOpen(false)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <g clip-path="url(#clip0_590_4911)">
                                                <path d="M5.7373 7.771H17.2156C17.4518 7.771 17.6783 7.67716 17.8454 7.51014C18.0124 7.34311 18.1062 7.11658 18.1062 6.88037C18.1062 6.64416 18.0124 6.41763 17.8454 6.2506C17.6783 6.08358 17.4518 5.98975 17.2156 5.98975H5.7373C5.5011 5.98975 5.27456 6.08358 5.10754 6.2506C4.94051 6.41763 4.84668 6.64416 4.84668 6.88037C4.84668 7.11658 4.94051 7.34311 5.10754 7.51014C5.27456 7.67716 5.5011 7.771 5.7373 7.771Z" fill="white" />
                                                <path d="M13.4403 12.4321C13.4403 12.1959 13.3464 11.9694 13.1794 11.8024C13.0124 11.6353 12.7859 11.5415 12.5496 11.5415H5.7373C5.5011 11.5415 5.27456 11.6353 5.10754 11.8024C4.94051 11.9694 4.84668 12.1959 4.84668 12.4321C4.84668 12.6683 4.94051 12.8949 5.10754 13.0619C5.27456 13.2289 5.5011 13.3228 5.7373 13.3228H12.5496C12.7859 13.3228 13.0124 13.2289 13.1794 13.0619C13.3464 12.8949 13.4403 12.6683 13.4403 12.4321Z" fill="white" />
                                                <path d="M19.0444 19.6049C18.7274 19.5562 18.4048 19.5562 18.0877 19.6049C17.9921 19.6253 17.9044 19.6729 17.8353 19.742C17.7661 19.8111 17.7185 19.8988 17.6982 19.9945C17.6494 20.3113 17.6494 20.6338 17.6982 20.9507C17.7187 21.0463 17.7664 21.1339 17.8355 21.203C17.9046 21.2721 17.9922 21.3197 18.0877 21.3402C18.4048 21.389 18.7274 21.389 19.0444 21.3402C19.14 21.3198 19.2275 21.2721 19.2965 21.203C19.3656 21.1339 19.4131 21.0462 19.4335 20.9507C19.4829 20.6339 19.4829 20.3113 19.4335 19.9945C19.4133 19.8988 19.3658 19.8111 19.2967 19.742C19.2277 19.6728 19.14 19.6252 19.0444 19.6049Z" fill="white" />
                                                <path d="M18.5659 19.0138C18.8021 19.0138 19.0287 18.9199 19.1957 18.7529C19.3627 18.5859 19.4565 18.3594 19.4565 18.1231V16.6372C19.4565 16.401 19.3627 16.1745 19.1957 16.0074C19.0287 15.8404 18.8021 15.7466 18.5659 15.7466C18.3297 15.7466 18.1032 15.8404 17.9362 16.0074C17.7691 16.1745 17.6753 16.401 17.6753 16.6372V18.1231C17.6753 18.3594 17.7691 18.5859 17.9362 18.7529C18.1032 18.9199 18.3297 19.0138 18.5659 19.0138Z" fill="white" />
                                                <path d="M18.565 14.9116C19.2878 14.9115 19.9943 15.1258 20.5952 15.5272C21.1962 15.9287 21.6646 16.4993 21.9412 17.167C22.2178 17.8347 22.2902 18.5694 22.1492 19.2783C22.0083 19.9871 21.6602 20.6382 21.1492 21.1492C20.6382 21.6603 19.9871 22.0083 19.2782 22.1492C18.5694 22.2902 17.8347 22.2178 17.167 21.9412C16.4993 21.6646 15.9287 21.1962 15.5272 20.5952C15.1257 19.9943 14.9115 19.2878 14.9116 18.5651C14.9123 17.5963 15.2975 16.6675 15.9825 15.9825C16.6675 15.2975 17.5963 14.9124 18.565 14.9116ZM18.565 13.1304C17.49 13.1304 16.4392 13.4492 15.5454 14.0464C14.6516 14.6436 13.9549 15.4925 13.5436 16.4857C13.1322 17.4789 13.0246 18.5717 13.2344 19.626C13.4441 20.6804 13.9618 21.6488 14.722 22.4089C15.4821 23.169 16.4506 23.6866 17.505 23.8963C18.5593 24.106 19.6522 23.9983 20.6453 23.5868C21.6384 23.1754 22.4873 22.4787 23.0844 21.5848C23.6816 20.6909 24.0003 19.6401 24.0002 18.5651C24.0002 17.8513 23.8596 17.1446 23.5865 16.4852C23.3133 15.8258 22.913 15.2267 22.4082 14.722C21.9035 14.2173 21.3044 13.817 20.645 13.5439C19.9855 13.2708 19.2788 13.1303 18.565 13.1304Z" fill="white" />
                                                <path d="M13.771 21.1271C13.0364 21.1683 12.2771 21.1899 11.4858 21.1908C9.37644 21.1885 7.48738 21.0441 5.7141 20.7497C4.92613 20.6091 4.12832 20.1666 3.46644 19.5052C2.80457 18.8438 2.36347 18.0469 2.22238 17.258C1.928 15.4866 1.78363 13.5985 1.78128 11.4844C1.77894 9.37034 1.928 7.48549 2.22238 5.71268C2.36347 4.92612 2.80503 4.12831 3.46878 3.46878C4.13253 2.80924 4.92847 2.36534 5.71878 2.22424C7.48644 1.92799 9.37503 1.78362 11.4844 1.78128C13.5938 1.77893 15.4833 1.92799 17.2566 2.22237C18.0469 2.36346 18.8438 2.80503 19.5052 3.46878C20.1666 4.13252 20.6086 4.92846 20.7497 5.71877C21.0441 7.48737 21.1875 9.37502 21.1908 11.4844C21.1908 12.2761 21.1683 13.0355 21.1271 13.7696C21.7772 14.118 22.35 14.5946 22.8108 15.1707C22.9046 14.1244 22.9702 12.9057 22.9721 11.4844C22.9688 8.80924 22.7447 6.85034 22.5033 5.41081C22.0843 3.03893 19.9299 0.884557 17.558 0.465494C16.1203 0.227369 14.1614 0.00283801 11.4844 2.55143e-05C8.80738 -0.00278699 6.85175 0.227369 5.41222 0.468775C3.04035 0.885963 0.885972 3.04034 0.468785 5.41221C0.227378 6.85174 0.00331585 8.81252 3.45996e-05 11.4844C-0.00324665 14.1563 0.227378 16.1203 0.468785 17.5599C0.887378 19.9317 3.04222 22.0861 5.4141 22.5052C6.85363 22.7447 8.81253 22.9688 11.4877 22.9739C12.9089 22.9739 14.1277 22.9083 15.1739 22.8127C14.597 22.3515 14.1198 21.7781 13.771 21.1271Z" fill="white" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_590_4911">
                                                    <rect width="24" height="24" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span>Terms & Conditions</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {isMemberDisplay && (
                    <div className="agent-name-sec">
                        {memberDisplayName}'s {stringsoflanguages.listings}
                    </div>
                )}

                <span className="listing-count">{stringsoflanguages.nooflistings}: {filteredListings.length}</span>
                {/* Member Cards Grid */}
                <div
                    className={`my-listings-cards-grid${gridHiddenForScrollRestore ? ' gm-grid--restore-hidden' : ''}`}
                    ref={listScrollRef}
                >
                    <div className="property-grid">

                        {filteredListings.length === 0 ? (
                            <p className="no-data-message">{searchQuery.trim() ? 'No listings match your search.' : 'No listings found.'}</p>
                        ) : (
                            filteredListings.map((listing, index) => {
                                const id = listing.listing_id ?? index;
                                const image = listing.image ?? 'https://placehold.co/600x400';
                                const title = [listing.PostalCode, listing.city_name].filter(Boolean).join(', ').trim() || '—';
                                const propertyType = listing.PropertyType ?? 'Property Type';
                                const streetPart = [listing.StreetNumber, listing.StreetName].filter(Boolean).join(' ').trim();
                                const cityParts = [listing.city_name, listing.state_name, listing.PostalCode, listing.country_name].filter(Boolean);
                                const cityPart = cityParts.join(', ').trim();
                                const address = streetPart ? (cityPart ? `${streetPart}, ${cityPart}` : streetPart) : (cityPart || '—');
                                const areaValue = listing.LivingArea ?? listing.LotSizeArea ?? null;
                                const rawUnit = listing.LivingAreaUnits ?? listing.LotSizeUnits ?? 'Sq Feet';
                                const areaUnitDisplay = /meter|metre/i.test(String(rawUnit)) ? 'm²' : 'f²';
                                const area = areaValue != null && areaValue !== '' ? `${areaValue} ${areaUnitDisplay}` : '—';
                                const bedrooms = listing.BedroomsTotal ?? '—';
                                const bathrooms = listing.BathroomsTotalInteger ?? '—';
                                const parking = listing.ParkingTotal ?? listing.GarageSpaces ?? '—';
                                const price = listing.ListPrice ?? '—';
                                const status = (listing.StandardStatus ?? listing.StandardStatusName) || '—';
                                const agentDetails = listing.agent_details ?? {};
                                const agent = {
                                    avatar: agentDetails.picture_thumb ?? agentDetails.picture_thumb ?? agentDetails.picture ?? '',
                                    name: [agentDetails.fname, agentDetails.lname].filter(Boolean).join(' ').trim() || '—'
                                };

                                return (
                                    <div
                                        key={id}
                                        className="property-card cursor-pointer"
                                        onClick={() => saveListingGridScrollAndGoToDetails(listing, id)}
                                    >

                                        <div className="property-image">
                                            {isMemberDisplay && (
                                                <button
                                                    type="button"
                                                    className={`favorite-btn ${listing.is_favorite == '1' ? 'active' : ''}`}
                                                    aria-label="Favorite"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        makeFav(listing.listing_id ?? listing.ListingId ?? id);
                                                    }}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
                                                        <path d="M0 5.85219C0 10.7151 4.01943 13.3065 6.96173 15.626C8 16.4445 9 17.2151 10 17.2151C11 17.2151 12 16.4445 13.0383 15.626C15.9806 13.3065 20 10.7151 20 5.85219C20 0.98925 14.4998 -2.45945 10 2.21572C5.50016 -2.45945 0 0.98925 0 5.85219Z" fill="white" />
                                                    </svg>
                                                </button>
                                            )}
                                            <img src={image} alt="property" />
                                            {/* <div className="status">{status}</div> */}
                                            <div className="agent-info">
                                                <div className="agent-avatar">
                                                    <img src={agent.avatar || 'https://placehold.co/40x40'} alt="agent" />
                                                </div>
                                                <div className="agent-name">
                                                    <span>{agent.name}</span>
                                                </div>
                                            </div>
                                            <div className="status">
                                                {status === 'Active' && (
                                                    <>
                                                        <span className="status-dot active"></span>
                                                        <span>{status}</span>
                                                    </>
                                                )}
                                                {status === 'Coming Soon' && (
                                                    <>
                                                        <span className="status-dot coming-soon"></span>
                                                        <span>{status}</span>
                                                    </>
                                                )}
                                                {status === 'Sold' && (
                                                    <>
                                                        <span className="status-dot sold"></span>
                                                        <span>{status}</span>
                                                    </>
                                                )}
                                                {status === 'Cancelled' && (
                                                    <>
                                                        <span className="status-dot cancelled"></span>
                                                        <span>{status}</span>
                                                    </>
                                                )}
                                                {status === 'Under Offer' && (
                                                    <>
                                                        <span className="status-dot under-offer"></span>
                                                        <span>{status}</span>
                                                    </>
                                                )}
                                                {(!status || !['Active', 'Coming Soon', 'Sold', 'Cancelled', 'Under Offer'].includes(status)) && (
                                                    <>
                                                        <span className="status-dot"></span>
                                                        <span>{status || 'N/A'}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className="property-content">

                                            <h3 className="property-title">{title}</h3>
                                            <div className="location">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M10.0001 1.81818C6.49118 1.81818 3.63647 4.47669 3.63647 7.74444C3.63647 11.7998 9.33131 17.7533 9.57377 18.0048C9.80151 18.241 10.1991 18.2406 10.4265 18.0048C10.6689 17.7533 16.3637 11.7998 16.3637 7.74444C16.3637 4.47669 13.509 1.81818 10.0001 1.81818ZM10.0001 10.7261C8.23468 10.7261 6.79844 9.38854 6.79844 7.74444C6.79844 6.10034 8.23472 4.7628 10.0001 4.7628C11.7655 4.7628 13.2017 6.10037 13.2017 7.74447C13.2017 9.38857 11.7655 10.7261 10.0001 10.7261Z" fill="black" fillOpacity="0.35" />
                                                </svg>
                                                <span>{address}</span>
                                            </div>
                                            <div className="property-details">
                                                <div className="detail-row">
                                                    <div className="detail-item">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                                                            <g clipPath="url(#clip0_405_733)">
                                                                <path d="M9.68521 11.0902L2.66029 7.27238C2.34781 7.10264 1.97881 7.10965 1.67304 7.29142C1.36727 7.47314 1.18481 7.794 1.18481 8.14972V17.0062C1.18481 17.3645 1.37832 17.6972 1.68985 17.8743L8.7149 21.868C8.86977 21.956 9.03911 22 9.20851 22C9.38112 22 9.55369 21.9543 9.71062 21.863C10.0214 21.6822 10.207 21.3595 10.207 20.9999V11.9676C10.207 11.6013 10.0071 11.2651 9.68521 11.0902Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M19.5402 4.96845C19.5397 4.59451 19.3332 4.25534 19.0012 4.08328L11.3408 0.111819C11.0507 -0.0386167 10.705 -0.0374991 10.416 0.114613L2.86825 4.08608C2.53961 4.25908 2.33519 4.59721 2.33472 4.96862C2.33429 5.33994 2.53789 5.67855 2.86606 5.85228L10.4139 9.84854C10.56 9.92587 10.7205 9.96455 10.881 9.96455C11.0396 9.96455 11.1982 9.92681 11.3429 9.8513L19.0035 5.85512C19.335 5.68216 19.5407 5.34239 19.5402 4.96845Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M20.3267 7.2914C20.0211 7.10963 19.6519 7.10245 19.3395 7.27232L12.3144 11.0902C11.9926 11.2651 11.7927 11.6013 11.7927 11.9675V20.9998C11.7927 21.3594 11.9783 21.6821 12.2891 21.8629C12.446 21.9543 12.6185 21.9999 12.7912 21.9999C12.9605 21.9999 13.13 21.9559 13.2848 21.8679L20.3099 17.8742C20.6214 17.6971 20.8149 17.3645 20.8149 17.0061V8.1497C20.8149 7.79398 20.6324 7.47313 20.3267 7.2914Z" fill="black" fillOpacity="0.35" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id={`clip0_405_733_${id}`}>
                                                                    <rect width="22" height="22" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <span>{area}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                                            <g clipPath={`url(#clip0_405_741_${id})`}>
                                                                <path d="M2.47178 13.9166V6.1042C2.47178 5.5634 2.03338 5.125 1.49263 5.125C0.951828 5.125 0.513428 5.5634 0.513428 6.1042V18.8958C0.513428 19.4366 0.951828 19.875 1.49263 19.875C2.03338 19.875 2.47178 19.4366 2.47178 18.8958V17.0614L22.5282 17.0142V18.8487C22.5282 19.3895 22.9666 19.8279 23.5074 19.8279C24.0481 19.8279 24.4865 19.3895 24.4865 18.8487V17.0142V14.4529V13.8695L2.47178 13.9166Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M24.4865 13.0779H9.60107V9.39035C9.60107 8.3203 10.4685 7.45285 11.5386 7.45285H20.575C22.7353 7.45285 24.4865 9.20405 24.4865 11.3643V13.0779Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M6.11706 12.656C7.4373 12.656 8.50756 11.5857 8.50756 10.2655C8.50756 8.94526 7.4373 7.875 6.11706 7.875C4.79683 7.875 3.72656 8.94526 3.72656 10.2655C3.72656 11.5857 4.79683 12.656 6.11706 12.656Z" fill="black" fillOpacity="0.35" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id={`clip0_405_741_${id}`}>
                                                                    <rect width="25" height="25" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <span>{bedrooms}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                                            <g clipPath={`url(#clip0_405_749_${id})`}>
                                                                <path d="M3.10162 23.8612C2.87721 24.1978 2.96818 24.6526 3.30475 24.8769C3.42965 24.9602 3.57076 25 3.71036 25C3.94703 25 4.17931 24.8855 4.32042 24.6737L4.87863 23.8364C4.42677 23.6381 3.99625 23.3999 3.59205 23.1255L3.10162 23.8612Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M21.4082 23.1255C21.004 23.3999 20.5735 23.6381 20.1216 23.8364L20.6798 24.6737C20.8209 24.8855 21.0531 25 21.2898 25C21.4294 25 21.5706 24.9602 21.6955 24.8769C22.0321 24.6526 22.123 24.1978 21.8986 23.8612L21.4082 23.1255Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M1.51367 14.1671V16.4551C1.51367 20.0898 4.47075 23.0469 8.10547 23.0469H16.8945C20.5292 23.0469 23.4863 20.0898 23.4863 16.4551V16.0286H11.3839C7.97895 16.0286 4.6646 15.4025 1.51367 14.1671Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M3.95483 8.63228V2.68555C3.95483 2.01245 4.50244 1.46484 5.17554 1.46484H6.1521C6.8252 1.46484 7.3728 2.01245 7.3728 2.68555V3.03306C6.24058 3.35928 5.41968 4.42187 5.41968 5.65601V5.85938C5.41968 6.26387 5.74761 6.5918 6.1521 6.5918H10.0583C10.4628 6.5918 10.7908 6.26387 10.7908 5.85938V5.61523C10.7908 4.38833 9.96348 3.35156 8.83765 3.03188V2.68555C8.83765 1.20474 7.63291 0 6.1521 0H5.17554C3.69473 0 2.48999 1.20474 2.48999 2.68555V8.04414C2.60532 8.07959 2.71919 8.12124 2.83096 8.1708C3.20249 8.33594 3.57725 8.4895 3.95483 8.63228Z" fill="black" fillOpacity="0.35" />
                                                                <path d="M23.3947 11.451H11.384C8.21198 11.451 5.13517 10.7979 2.23776 9.5101C1.45095 9.16156 0.53244 9.51469 0.183368 10.2999C-0.165705 11.0852 0.187909 12.0053 0.973211 12.3543C4.2714 13.8205 7.77375 14.5638 11.3841 14.5638H23.3948C24.2546 14.5638 24.9512 13.8671 24.9512 13.0074C24.9511 12.1476 24.2545 11.451 23.3947 11.451Z" fill="black" fillOpacity="0.35" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id={`clip0_405_749_${id}`}>
                                                                    <rect width="25" height="25" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <span>{bathrooms}</span>
                                                    </div>
                                                    <div className="detail-item">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                                            <path d="M3.12492 20.5448V21.875C3.12492 22.1512 3.23467 22.4162 3.43001 22.6116C3.62537 22.8069 3.89031 22.9167 4.16659 22.9167H5.20825C5.48452 22.9167 5.74947 22.8069 5.94483 22.6116C6.14017 22.4162 6.24992 22.1512 6.24992 21.875V20.8333H18.7499V21.875C18.7499 22.1512 18.8597 22.4162 19.055 22.6116C19.2503 22.8069 19.5153 22.9167 19.7916 22.9167H20.8333C21.1095 22.9167 21.3745 22.8069 21.5698 22.6116C21.7651 22.4162 21.8749 22.1512 21.8749 21.875V20.5448C22.191 20.3635 22.4537 20.1022 22.6365 19.7871C22.8194 19.4721 22.916 19.1143 22.9166 18.75V15.625C22.9166 14.7594 22.3885 14.0187 21.6374 13.7031L20.2124 9.42812C20.0058 8.8057 19.608 8.26426 19.0761 7.88073C18.5441 7.4972 17.9047 7.29107 17.2489 7.29166H7.75096C7.09512 7.29107 6.45576 7.4972 5.92375 7.88073C5.39174 8.26426 4.99414 8.8057 4.78742 9.42812L3.36242 13.7031C2.98356 13.8618 2.66003 14.1288 2.43245 14.4708C2.20488 14.8127 2.08339 15.2143 2.08325 15.625V18.75C2.08325 19.5187 2.50409 20.1833 3.12492 20.5448ZM5.72909 18.75C5.52383 18.7499 5.3206 18.7095 5.13098 18.6308C4.94138 18.5522 4.76911 18.437 4.62401 18.2919C4.33099 17.9986 4.16645 17.601 4.16659 17.1865C4.16672 16.7719 4.33153 16.3744 4.62475 16.0813C4.91797 15.7883 5.31559 15.6238 5.73013 15.6239C6.14467 15.6241 6.54217 15.7888 6.8352 16.0821C7.12822 16.3753 7.29276 16.7729 7.29263 17.1875C7.29249 17.6021 7.12768 17.9996 6.83446 18.2926C6.54124 18.5856 6.14363 18.7501 5.72909 18.75ZM19.2708 18.75C18.8562 18.7499 18.4587 18.5851 18.1656 18.2919C17.8726 17.9986 17.7081 17.601 17.7083 17.1865C17.7084 16.7719 17.8731 16.3744 18.1664 16.0813C18.4596 15.7883 18.8572 15.6238 19.2718 15.6239C19.6864 15.6241 20.0839 15.7888 20.3769 16.0821C20.6699 16.3753 20.8344 16.7729 20.8343 17.1875C20.8342 17.6021 20.6694 17.9996 20.3762 18.2926C20.0829 18.5856 19.6853 18.7501 19.2708 18.75ZM7.75096 9.37499H17.2478C17.4666 9.37494 17.6797 9.44373 17.8572 9.57162C18.0346 9.6995 18.1673 9.87999 18.2364 10.0875L19.3885 13.5417H5.61138L6.76242 10.0875C6.90513 9.66145 7.30304 9.37499 7.75096 9.37499Z" fill="black" fillOpacity="0.35" />
                                                            <path d="M22.9166 7.69585V5.49898L12.8291 2.13648C12.6062 2.06186 12.3645 2.06516 12.1437 2.14586L2.08325 5.80419V8.02085L12.5155 4.22711L22.9166 7.69585Z" fill="black" fillOpacity="0.35" />
                                                        </svg>
                                                        <span>{parking}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="price-type">
                                                <span className="price">{price !== '—' && price !== '' && !isNaN(Number(price)) ? `€ ${Number(price).toLocaleString()}` : price}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>


                </div>

                {/* Desktop only: gear icon with dropdown */}
                {!isMobile && (
                    <div className='btn-section btn-section-gear' ref={gearMenuRef}>
                        <button
                            type="button"
                            className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                            onClick={() => {
                                setGearMenuOpen((prev) => !prev);
                                setGearSpinning(true);
                                setTimeout(() => setGearSpinning(false), 500);
                            }}
                            aria-label="Options"
                            aria-expanded={gearMenuOpen}
                        >
                            <i className="fa-solid fa-gear"></i>
                        </button>
                        {gearMenuOpen && (
                            <ul className="gear-dropdown" aria-label="Options list">
                                <li>
                                    <button type="button" className="gear-dropdown-item" onClick={() => { setGearMenuOpen(false); navigate('/home/inactive-listing'); }}>
                                        <i className="fa-solid fa-ban"></i>
                                        <span>{stringsoflanguages.inactiveListings}</span>
                                    </button>
                                </li>
                                <li>
                                    <button type="button" className="gear-dropdown-item" onClick={() => { setGearMenuOpen(false); navigate('/home/add-listing'); }}>
                                        <i className="fa-solid fa-plus"></i>
                                        <span>{stringsoflanguages.addListings}</span>
                                    </button>
                                </li>
                                <li>
                                    <button type="button" className="gear-dropdown-item" onClick={() => { setGearMenuOpen(false); navigate('/home/my-listing-contract'); }}>
                                        <i className="fa-solid fa-book"></i>
                                        <span>{stringsoflanguages.termsAndConditions}</span>
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile: bottom nav (same style as Details / GlobalPortfolio) */}
            {isMobile && (
                <nav className="mobile-bottom-nav liquidGlass-wrapper dock my-listings-footer-nav" aria-label="Listings actions">
                    <div className="liquidGlass-effect" aria-hidden="true" />
                    <div className="liquidGlass-shine" aria-hidden="true" />
                    <div className="mobile-nav-item" aria-label="Cancel" onClick={() => { setGearMenuOpen(false); navigate('/home/inactive-listing'); }}>
                        <i className="fa-solid fa-ban"></i>
                    </div>
                    <div className="mobile-nav-item" aria-label="Add" onClick={() => { setGearMenuOpen(false); navigate('/home/add-listing'); }}>
                        <i className="fa-solid fa-plus"></i>
                    </div>
                    <div className="mobile-nav-item" aria-label="Book" onClick={() => { setGearMenuOpen(false); navigate('/home/my-listing-contract'); }}>
                        <i className="fa-solid fa-book"></i>
                    </div>
                </nav>
            )}
            <svg style={{ display: 'none' }} aria-hidden="true">
                <filter id="glass-distortion-my-listings" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01 0.01" numOctaves="1" seed="5" result="turbulence" />
                    <feComponentTransfer in="turbulence" result="mapped">
                        <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                        <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                        <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                    </feComponentTransfer>
                    <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                    <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                        <fePointLight x="-200" y="-200" z="300" />
                    </feSpecularLighting>
                    <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                    <feDisplacementMap in="SourceGraphic" in2="softMap" scale="150" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>
        </React.Fragment>
    )
}